import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'

import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { type BrazeBanner, type BrazeSdk, loadBrazeSdk } from '@/plugins/braze'

const REFRESH_WINDOW_MS = 60_000
const MAX_REFRESHES_PER_WINDOW = 3
const INIT_MAX_ATTEMPTS = 3
const INIT_RETRY_DELAY_MS = 5_000

const delay = async (ms: number): Promise<void> =>
  await new Promise((resolve) => setTimeout(resolve, ms))

export const useBrazeStore = defineStore('braze', () => {
  // brazeInitialized is the in-progress lock: it flips true at the START of
  // init (before the SDK is loaded) to guard against re-entry, so it does NOT
  // mean the SDK handle is usable. brazeReady is the readiness signal — it flips
  // true only once `braze` is assigned and the session is open, and back to
  // false on teardown. Consumers that call the banner primitives must gate on
  // brazeReady, never brazeInitialized.
  const brazeInitialized = ref(false)
  const brazeReady = ref(false)
  const authStore = useAuthStore()

  let braze: BrazeSdk | null = null
  const recentRefreshTimestamps: number[] = []

  // Isolates each init attempt from a later one. brazeInitialized doubles as
  // the in-progress lock and the shutdown sentinel, so a logout+login during
  // the retry delay would otherwise let a stale attempt resume and initialize
  // the already-live SDK, stranding the store (braze non-null while the
  // sentinel reads false, making shutdownBraze a silent no-op).
  let initGeneration = 0

  // The token endpoint is the single place the Braze WebSDK enablement decision
  // is made (server-side, secret-gated). A null return is its definitive "Braze
  // is off for this user" answer and must be treated as a clean stop — distinct
  // from a thrown transient failure, which callers may retry.
  const fetchBrazeToken = async (): Promise<string | null> => {
    try {
      const response = await OlympusClient.auth.v2.hermes.getBrazeToken({})
      return response.token
    } catch (error) {
      if (
        OlympusClient.auth.v2.hermes.getBrazeToken.isError(error) &&
        error.code === 'braze-disabled'
      ) {
        return null
      }
      throw error
    }
  }

  const refreshBrazeToken = async (): Promise<void> => {
    if (!braze) return
    const gen = initGeneration

    const cutoff = Date.now() - REFRESH_WINDOW_MS
    let oldest = recentRefreshTimestamps[0]
    while (oldest !== undefined && oldest < cutoff) {
      recentRefreshTimestamps.shift()
      oldest = recentRefreshTimestamps[0]
    }
    if (recentRefreshTimestamps.length >= MAX_REFRESHES_PER_WINDOW) return
    recentRefreshTimestamps.push(Date.now())

    try {
      const token = await fetchBrazeToken()
      // Ignore a stale refresh whose SDK was torn down or replaced mid-fetch —
      // it must never destroy or re-sign a newer generation's SDK.
      if (!braze || initGeneration !== gen) return
      // Backend now reports Braze disabled: release this SDK rather than leave
      // it running with an unrenewable, failing auth signature.
      if (token === null) {
        teardownBraze()
        return
      }
      braze.setSdkAuthenticationSignature(token)
    } catch (e) {
      logError(e)
    }
  }

  const teardownBraze = (): void => {
    try {
      braze?.destroy()
    } catch (e) {
      logError(e)
    } finally {
      braze = null
      recentRefreshTimestamps.length = 0
      brazeInitialized.value = false
      brazeReady.value = false
    }
  }

  // A transient first-login failure would otherwise strand Braze for the whole
  // session: the lifecycle watcher only re-fires on user change.
  const loadSdkWithRetry = async (
    gen: number
  ): Promise<{
    sdk: BrazeSdk
    token: string
  } | null> => {
    for (let attempt = 1; attempt <= INIT_MAX_ATTEMPTS; attempt++) {
      try {
        // Token first: a null (disabled) response short-circuits before loading
        // the SDK bundle. Only our own in-flight attempt may reset state — a
        // superseded attempt must not clear a newer init's lock (see catch).
        const token = await fetchBrazeToken()
        if (token === null) {
          if (brazeInitialized.value && initGeneration === gen) teardownBraze()
          return null
        }
        const sdk = await loadBrazeSdk()
        return { sdk, token }
      } catch (e) {
        logError(e)
        if (!brazeInitialized.value || initGeneration !== gen) return null
        if (attempt === INIT_MAX_ATTEMPTS) {
          teardownBraze()
          return null
        }
        await delay(INIT_RETRY_DELAY_MS)
        if (!brazeInitialized.value || initGeneration !== gen) return null
      }
    }
    return null
  }

  const initializeBraze = async (): Promise<void> => {
    if (brazeInitialized.value) return
    if (!authStore.isAuthenticated || !authStore.user) return

    const apiKey = import.meta.env.VITE_BRAZE_API_KEY
    const baseUrl = import.meta.env.VITE_BRAZE_SDK_ENDPOINT
    if (!apiKey || !baseUrl) return

    const userId = authStore.user.id
    const gen = ++initGeneration
    brazeInitialized.value = true

    const loaded = await loadSdkWithRetry(gen)
    if (!loaded || !brazeInitialized.value || initGeneration !== gen) return

    const { sdk, token } = loaded
    // allowUserSuppliedJavascript is required for Banner rendering: insertBanner
    // no-ops without it (Banner content is HTML/JS authored in the Braze
    // dashboard). Set once at init since options are fixed for the SDK's life.
    const ok = sdk.initialize(apiKey, {
      allowUserSuppliedJavascript: true,
      baseUrl,
      enableSdkAuthentication: true,
    })
    if (!ok) {
      brazeInitialized.value = false
      return
    }

    braze = sdk
    sdk.subscribeToSdkAuthenticationFailures(() => {
      void refreshBrazeToken()
    })

    sdk.changeUser(userId, token)
    sdk.openSession()
    brazeReady.value = true
  }

  const shutdownBraze = (): void => {
    if (!brazeInitialized.value) return
    teardownBraze()
  }

  // Banner primitives — thin, SDK-guarded pass-throughs so components never
  // touch the private `braze` handle or the lazily-loaded module directly.
  const subscribeToBannerUpdates = (
    onUpdate: (banners: Record<string, BrazeBanner | null>) => void
  ): (() => void) => {
    if (!braze) return () => {}
    const subscriptionId = braze.subscribeToBannersUpdates(onUpdate)
    if (subscriptionId === undefined) return () => {}
    return () => braze?.removeSubscription(subscriptionId)
  }

  const requestBanners = (placementIds: string[]): void => {
    braze?.requestBannersRefresh(placementIds)
  }

  const getBanner = (placementId: string): BrazeBanner | null =>
    braze?.getBanner(placementId) ?? null

  const insertBanner = (banner: BrazeBanner, container: HTMLElement): void => {
    braze?.insertBanner(banner, container)
  }

  return {
    brazeInitialized,
    brazeReady,
    getBanner,
    initializeBraze,
    insertBanner,
    refreshBrazeToken,
    requestBanners,
    shutdownBraze,
    subscribeToBannerUpdates,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBrazeStore, import.meta.hot))
}
